import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import Modal from '../../misc/Modal';
import { formulas } from '.';
import FieldFormulaEditor from './FieldFormulaEditor';
import { 
  groupFieldsByPage, 
  getGeneralWarnings,
  formatValue
} from './modalHelpers';
import { generateObjectId } from '@builttocreate/engine-utils';

const StyledContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
`;

const StyledHeader = styled.div`
  flex-shrink: 0;
  padding: 0 0 16px 0;
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 16px;
`;

const StyledHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const StyledTabContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledTab = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  background: ${props => props.active ? 'rgb(231, 235, 241)' : 'transparent'};
  color: ${props => props.active ? 'rgb(69, 82, 98)' : '#64748b'};
  cursor: pointer;
  border-radius: 8px;
  font-size: 16px;
  font-weight: ${props => props.active ? '600' : '500'};
  transition: all 0.15s ease;
  
  &:hover {
    background: ${props => props.active ? 'rgb(224, 227, 233)' : 'rgb(248, 250, 252)'};
    color: ${props => props.active ? 'rgb(69, 82, 98)' : '#1e293b'};
  }
`;

const StyledCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background:rgb(231, 235, 241);
  color:rgb(69, 82, 98);
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.15s ease;
  
  &:hover {
    background-color:rgb(224, 227, 233);
    color: #0f172a;
  }
`;

const StyledStatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 500;
  line-height: 20px;
  background-color: ${props => {
    if (props.status === 'valid') return '#dcfce7';
    if (props.status === 'warning') return '#fef3c7';
    if (props.status === 'error') return '#fee2e2';
    return '#f1f5f9';
  }};
  color: ${props => {
    if (props.status === 'valid') return '#15803d';
    if (props.status === 'warning') return '#a16207';
    if (props.status === 'error') return '#dc2626';
    return '#475569';
  }};
`;

const StyledSearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StyledSearchInput = styled.input`
  padding: 6px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  color: #1e293b;
  background-color: #ffffff;
  flex: 1;
  min-width: 200px;
  
  &:focus {
    outline: none;
    border-color: #0ea5e9;
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
  }
  
  &::placeholder {
    color: #94a3b8;
  }
`;

const StyledExecutionTime = styled.b`
  font-size: 11px;
  color: #64748b;
  margin-top: 2px;
`;

const StyledScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding-right: 4px;
  margin-right: -4px;
`;

// Joydoc tab specific styles
const StyledJoydocContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledEditButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background-color: #0ea5e9;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background-color: #0284c7;
  }
  
  &.cancel {
    background-color: #64748b;
    
    &:hover {
      background-color: #475569;
    }
  }
  
  &.save {
    background-color: #059669;
    
    &:hover {
      background-color: #047857;
    }
  }
`;

const StyledJoydocContent = styled.div`
  height: 730px;
`;

const StyledJsonDisplay = styled.pre`
  height: 98%;
  margin: 0;
  padding: 16px;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-family: 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 12px;
  color: #1e293b;
  overflow: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

const StyledJsonEditor = styled.textarea`
  width: 100%;
  height: 100%;
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-family: 'SF Mono', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 12px;
  color: #1e293b;
  background-color: #ffffff;
  resize: none;
  
  &:focus {
    outline: none;
    border-color: #0ea5e9;
    box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
  }
`;

const StyledButtonGroup = styled.div`
  display: flex;
  gap: 6px;
`;

const StyledPageSection = styled.div`
  margin-bottom: 24px;
`;

const StyledPageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
  padding: 0 2px;
`;

const StyledPageTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
`;

const StyledFieldCount = styled.span`
  font-size: 11px;
  color: #64748b;
  background-color: #f1f5f9;
  padding: 2px 6px;
  border-radius: 12px;
`;

// Field-related styles moved to FieldFormulaEditor component

// Value comparison and action styles moved to FieldFormulaEditor component

const StyledFooter = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0 0 0;
  border-top: 1px solid #e2e8f0;
  background-color: #fafafa;
`;

const StyledSummary = styled.span`
  font-size: 12px;
  color: #64748b;
`;

const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  background-color: #0ea5e9;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover:not(:disabled) {
    background-color: #0284c7;
  }
  
  &:disabled {
    background-color: #cbd5e1;
    cursor: not-allowed;
  }
  
  &.apply {
    background-color: #059669;
    
    &:hover {
      background-color: #047857;
    }
  }
  
  &.save {
    background-color: #7c3aed;
    
    &:hover {
      background-color: #6d28d9;
    }
  }
  
  &.cancel {
    background-color: #ef4444;
    
    &:hover {
      background-color: #dc2626;
    }
  }
`;

const StyledNoFields = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: #64748b;
`;

const StyledNoFieldsIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const StyledNoFieldsText = styled.div`
  font-size: 14px;
  margin-bottom: 8px;
`;

const StyledNoFieldsSubtext = styled.div`
  font-size: 12px;
  opacity: 0.8;
`;

// Field expand button and content styles moved to FieldFormulaEditor component

const StyledGeneralWarningsAccordion = styled.div`
  margin-bottom: 16px;
  border: 1px solid #f59e0b;
  border-radius: 12px;
  background-color: #fffbeb;
  overflow: hidden;
`;

const StyledAccordionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background-color: #fef3c7;
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #92400e;
  transition: background-color 0.15s ease;
  
  &:hover {
    background-color: #fde68a;
  }
`;

const StyledAccordionIcon = styled.span`
  font-size: 12px;
  transition: transform 0.15s ease;
  transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const StyledAccordionContent = styled.div`
  padding: ${props => props.expanded ? '16px' : '0 16px'};
  max-height: ${props => props.expanded ? '400px' : '0'};
  overflow: hidden;
  transition: all 0.15s ease;
`;

const StyledGeneralWarningItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px solid #fde68a;
  
  &:last-child {
    border-bottom: none;
  }
`;

const StyledGeneralWarningIcon = styled.span`
  color: #f59e0b;
  font-size: 14px;
  flex-shrink: 0;
  margin-top: 2px;
`;

const StyledGeneralWarningContent = styled.div`
  flex: 1;
`;

const StyledGeneralWarningMessage = styled.div`
  font-size: 13px;
  color: #92400e;
  line-height: 1.4;
  margin-bottom: 4px;
`;

const StyledGeneralWarningDetails = styled.div`
  font-size: 11px;
  color: #78350f;
  opacity: 0.8;
`;

const StyledGeneralWarningType = styled.span`
  font-size: 10px;
  font-weight: 500;
  color: #78350f;
  background-color: #fbbf24;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: auto;
  flex-shrink: 0;
`;

const StyledNonReadableIdsWarning = styled.div`
  background-color: #fef2f2;
  border: 1px solid #fca5a5;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StyledWarningIcon = styled.span`
  color: #dc2626;
  font-size: 20px;
  flex-shrink: 0;
`;

const StyledWarningText = styled.div`
  color: #991b1b;
  font-size: 13px;
  line-height: 1.4;
`;

export default function FormulaTestsModal({ 
  isOpen, 
  onClose, 
  beforeDoc, 
  afterResult,
  executionTime,
  onApplyChanges,
  useReadableIds = false
}) {
  const [activeTab, setActiveTab] = useState('formula');
  const [isResolved, setIsResolved] = useState(false);
  const [resolvedResult, setResolvedResult] = useState(null);
  const [resolveExecutionTime, setResolveExecutionTime] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [isEditingJoydoc, setIsEditingJoydoc] = useState(false);
  const [editedJoydoc, setEditedJoydoc] = useState('');
  const [isGeneralWarningsExpanded, setIsGeneralWarningsExpanded] = useState(true);
  const [expandedFields, setExpandedFields] = useState(new Set());
  
  // Track which fields have unsaved changes
  const [fieldsWithUnsavedChanges, setFieldsWithUnsavedChanges] = useState(new Set());
  
  // Centralized state for all pending formula changes
  // This ensures we don't lose changes when one field is saved
  const [pendingFormulaChanges, setPendingFormulaChanges] = useState(new Map());
  
  // Reset key to force field editors to re-mount after cancel all
  const [resetKey, setResetKey] = useState(0);
  
  // Internal document state for Joydoc tab - separate from beforeDoc prop
  const [internalDoc, setInternalDoc] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setActiveTab('formula');
      setIsResolved(!!afterResult);
      setResolvedResult(afterResult);
      setResolveExecutionTime(executionTime || 0);
      setSearchQuery('');
      setIsEditingJoydoc(false);
      setEditedJoydoc(beforeDoc ? JSON.stringify(beforeDoc, null, 2) : '');
      setIsGeneralWarningsExpanded(true);
      setInternalDoc(beforeDoc); // Initialize internal doc state
      
      // Initialize expanded fields - expand fields that already have formulas
      if (beforeDoc) {
        const fieldsWithFormulas = new Set();
        if (beforeDoc.fields) {
          beforeDoc.fields.forEach(field => {
            if (field.formulas && field.formulas.length > 0) {
              fieldsWithFormulas.add(field._id);
            }
          });
        }
        setExpandedFields(fieldsWithFormulas);
      }
    } else {
      // Reset all state when modal closes
      setActiveTab('formula');
      setIsResolved(false);
      setResolvedResult(null);
      setResolveExecutionTime(0);
      setSearchQuery('');
      setIsEditingJoydoc(false);
      setEditedJoydoc('');
      setIsGeneralWarningsExpanded(true);
      setExpandedFields(new Set());
      setFieldsWithUnsavedChanges(new Set());
      setPendingFormulaChanges(new Map());
      setInternalDoc(null);
    }
  }, [isOpen, afterResult, executionTime, beforeDoc]);

  // Handle Test button - resolves formulas without saving
  const handleTest = () => {
    const currentDoc = internalDoc || beforeDoc;
    if (!currentDoc) return;
    
    const startTime = performance.now();
    
    // Create a copy of the document with pending changes applied
    let docToResolve = JSON.parse(JSON.stringify(currentDoc));
    
    // Apply all pending formula changes before resolving
    pendingFormulaChanges.forEach((change, fieldId) => {
      const field = docToResolve.fields?.find(f => f._id === fieldId);
      if (!field) return;
      
      if (change.remove) {
        // Remove formula from field
        delete field.formulas;
      } else if (change.isNewFormula) {
        // Create new formula
        if (!docToResolve.formulas) {
          docToResolve.formulas = [];
        }
        
        // Add new formula
        docToResolve.formulas.push({
          _id: change.formulaId,
          desc: '',
          type: 'calc',
          scope: 'global',
          expression: change.expression
        });
        
        // Assign formula to field
        if (!field.formulas) {
          field.formulas = [];
        }
        field.formulas.push({
          _id: generateObjectId(),
          formula: change.formulaId,
          key: 'value'
        });
      } else {
        // Update existing formula
        const formulaDef = docToResolve.formulas?.find(f => f._id === change.formulaId);
        if (formulaDef) {
          formulaDef.expression = change.expression;
        }
      }
    });
    
    // Resolve with the document including pending changes
    const result = formulas.resolve(docToResolve);
    const execTime = Math.round(performance.now() - startTime);
    
    setResolvedResult(result);
    setResolveExecutionTime(execTime);
    setIsResolved(true);
  };

  // Handle Save button - saves changes externally
  const handleSave = () => {
    if (!onApplyChanges) return;
    
    // Create a copy of the current document (use internal doc if available)
    const currentDoc = internalDoc || beforeDoc;
    let updatedDoc = JSON.parse(JSON.stringify(currentDoc));
    
    // Apply all pending changes
    pendingFormulaChanges.forEach((change, fieldId) => {
      const field = updatedDoc.fields?.find(f => f._id === fieldId);
      if (!field) return;
      
      if (change.remove) {
        // Remove formula from field
        delete field.formulas;
      } else if (change.isNewFormula) {
        // Create new formula
        if (!updatedDoc.formulas) {
          updatedDoc.formulas = [];
        }
        
        // Add new formula
        updatedDoc.formulas.push({
          _id: change.formulaId,
          desc: '',
          type: 'calc',
          scope: 'global',
          expression: change.expression
        });
        
        // Assign formula to field
        if (!field.formulas) {
          field.formulas = [];
        }
        field.formulas.push({
          _id: generateObjectId(),
          formula: change.formulaId,
          key: 'value'
        });
      } else {
        // Update existing formula
        const formulaDef = updatedDoc.formulas?.find(f => f._id === change.formulaId);
        if (formulaDef) {
          formulaDef.expression = change.expression;
        }
      }
    });
    
    // Apply changes externally
    onApplyChanges(updatedDoc);
    
    // Update internal document state with the changes
    setInternalDoc(updatedDoc);
    
    // Clear all pending changes since they're now saved
    setPendingFormulaChanges(new Map());
    setFieldsWithUnsavedChanges(new Set());
    
    // Close the modal after saving
    onClose();
  };

  // Handle canceling all formula changes
  const handleCancelAllFormulaChanges = () => {
    if (fieldsWithUnsavedChanges.size === 0) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to cancel all ${fieldsWithUnsavedChanges.size} unsaved change${fieldsWithUnsavedChanges.size !== 1 ? 's' : ''}?`
    );
    
    if (confirmed) {
      // Clear all pending changes
      setPendingFormulaChanges(new Map());
      setFieldsWithUnsavedChanges(new Set());
      
      // Force field editors to reset by incrementing reset key
      // This causes all field editors to re-mount and reset to original values
      setResetKey(prev => prev + 1);
    }
  };

  const toggleFieldExpansion = React.useCallback((fieldId) => {
    setExpandedFields(prevExpanded => {
      const newExpanded = new Set(prevExpanded);
      if (newExpanded.has(fieldId)) {
        newExpanded.delete(fieldId);
      } else {
        newExpanded.add(fieldId);
      }
      return newExpanded;
    });
  }, []);

  const handleCancelJoydocEdit = () => {
    setIsEditingJoydoc(false);
    // Use internal doc state instead of beforeDoc
    const docToEdit = internalDoc || beforeDoc;
    setEditedJoydoc(docToEdit ? JSON.stringify(docToEdit, null, 2) : '');
  };

  const handleSaveJoydocEdit = () => {
    try {
      const parsedDoc = JSON.parse(editedJoydoc);
      // Only update internal state, not the external document
      setInternalDoc(parsedDoc);
      setIsEditingJoydoc(false);
      // Reset all states since document changed
      setIsResolved(false);
      setResolvedResult(null);
      
      // Re-initialize expanded fields for the new document
      const fieldsWithFormulas = new Set();
      if (parsedDoc.fields) {
        parsedDoc.fields.forEach(field => {
          if (field.formulas && field.formulas.length > 0) {
            fieldsWithFormulas.add(field._id);
          }
        });
      }
      setExpandedFields(fieldsWithFormulas);
    } catch (error) {
      alert('Invalid JSON format. Please check your syntax.');
    }
  };

  // Check if there are unsaved changes in Joydoc tab
  const hasJoydocChanges = () => {
    if (!isEditingJoydoc) return false;
    // Check against internal doc state
    const currentDoc = internalDoc || beforeDoc;
    const currentJson = currentDoc ? JSON.stringify(currentDoc, null, 2) : '';
    return editedJoydoc !== currentJson;
  };

  // Handle close with warning for unsaved changes
  const handleCloseWithWarning = () => {
    // Check for unsaved changes regardless of whether test was run
    if (pendingFormulaChanges.size > 0 || fieldsWithUnsavedChanges.size > 0) {
      const confirmed = window.confirm(
        `You have unsaved changes in ${fieldsWithUnsavedChanges.size} field${fieldsWithUnsavedChanges.size !== 1 ? 's' : ''}. Are you sure you want to close without saving?`
      );
      if (!confirmed) return;
    }
    
    // Check for unsaved changes in Joydoc tab
    if (activeTab === 'joydoc' && hasJoydocChanges()) {
      const confirmed = window.confirm(
        'You have unsaved changes in the Joydoc editor. Are you sure you want to close without saving?'
      );
      if (!confirmed) return;
    }
    
    onClose();
  };
  
  // Handle field unsaved changes tracking
  const handleFieldUnsavedChange = React.useCallback((fieldId, hasChanges) => {
    setFieldsWithUnsavedChanges(prevUnsavedFields => {
      const newUnsavedFields = new Set(prevUnsavedFields);
      if (hasChanges) {
        newUnsavedFields.add(fieldId);
      } else {
        newUnsavedFields.delete(fieldId);
      }
      return newUnsavedFields;
    });
  }, []);
  
  // Handle formula expression changes from field editors
  const handleFormulaExpressionChange = React.useCallback((fieldId, expression, isNewFormula, formulaId) => {
    setPendingFormulaChanges(prevChanges => {
      const newChanges = new Map(prevChanges);
      if (expression !== null) {
        newChanges.set(fieldId, { expression, isNewFormula, formulaId });
      } else {
        newChanges.delete(fieldId);
      }
      return newChanges;
    });
  }, []);

  // Handle formula removal
  const handleRemoveFormula = React.useCallback((fieldId) => {
    // Add a pending change to remove the formula
    setPendingFormulaChanges(prevChanges => {
      const newChanges = new Map(prevChanges);
      newChanges.set(fieldId, { expression: '', isNewFormula: false, formulaId: null, remove: true });
      return newChanges;
    });
    
    // Mark field as having unsaved changes
    setFieldsWithUnsavedChanges(prevUnsavedFields => {
      const newUnsavedFields = new Set(prevUnsavedFields);
      newUnsavedFields.add(fieldId);
      return newUnsavedFields;
    });
  }, []);

  // Calculate derived values
  const generalWarnings = useMemo(() => getGeneralWarnings(resolvedResult), [resolvedResult]);

  const pages = useMemo(() => {
    const currentDoc = internalDoc || beforeDoc;
    return currentDoc ? groupFieldsByPage(currentDoc) : [];
  }, [internalDoc, beforeDoc]);
  
  // Create a document with pending changes applied for display in Joydoc tab
  const docWithPendingChanges = useMemo(() => {
    const currentDoc = internalDoc || beforeDoc;
    if (!currentDoc || pendingFormulaChanges.size === 0) return currentDoc;
    
    // Create a copy and apply pending changes
    const updatedDoc = JSON.parse(JSON.stringify(currentDoc));
    
    pendingFormulaChanges.forEach((change, fieldId) => {
      const field = updatedDoc.fields?.find(f => f._id === fieldId);
      if (!field) return;
      
      if (change.remove) {
        // Remove formula from field
        delete field.formulas;
      } else if (change.isNewFormula) {
        // Create new formula
        if (!updatedDoc.formulas) {
          updatedDoc.formulas = [];
        }
        
        // Add new formula if it doesn't exist
        if (!updatedDoc.formulas.find(f => f._id === change.formulaId)) {
          updatedDoc.formulas.push({
            _id: change.formulaId,
            desc: '',
            type: 'calc',
            scope: 'global',
            expression: change.expression
          });
        }
        
        // Assign formula to field
        if (!field.formulas) {
          field.formulas = [];
        }
        field.formulas = [{
          _id: generateObjectId(),
          formula: change.formulaId,
          key: 'value'
        }];
      } else {
        // Update existing formula
        const formulaDef = updatedDoc.formulas?.find(f => f._id === change.formulaId);
        if (formulaDef) {
          formulaDef.expression = change.expression;
        }
      }
    });
    
    return updatedDoc;
  }, [internalDoc, beforeDoc, pendingFormulaChanges]);
  
  // Filter pages based on search query
  const filteredPages = useMemo(() => {
    if (!searchQuery.trim()) return pages;
    
    const query = searchQuery.toLowerCase().trim();
    return pages.map(page => ({
      ...page,
      fields: page.fields.filter(field => 
        (field.title && field.title.toLowerCase().includes(query)) ||
        field._id.toLowerCase().includes(query)
      )
    })).filter(page => page.fields.length > 0);
  }, [pages, searchQuery]);

  const totalFields = filteredPages.reduce((sum, page) => sum + page.fields.length, 0);
  const changedFields = isResolved ? filteredPages.reduce((sum, page) => {
    const changedCount = page.fields.filter(field => {
      const currentDoc = internalDoc || beforeDoc;
      const beforeField = currentDoc.fields?.find(f => f._id === field._id);
      const afterField = resolvedResult.doc?.fields?.find(f => f._id === field._id);
      if (!beforeField || !afterField) return false;
      return JSON.stringify(beforeField.value) !== JSON.stringify(afterField.value);
    }).length;
    return sum + changedCount;
  }, 0) : 0;

  const currentStatus = resolvedResult?.status || 'preview';
  const currentWarnings = resolvedResult?.warnings || [];

  const currentDoc = internalDoc || beforeDoc;
  if (!isOpen || !currentDoc) {
    return null;
  }
  
  const handleEditJoydoc = () => {
    setIsEditingJoydoc(true);
    // Use document with pending changes
    setEditedJoydoc(docWithPendingChanges ? JSON.stringify(docWithPendingChanges, null, 2) : '');
  };

  return (
    <Modal
      isOpen={isOpen}
      title=""
      width={1000}
      height={750}
      contentStyles={{ 
        backgroundColor: '#fafafa', 
        borderRadius: '16px', 
        padding: '18px',
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden'
      }}
    >
      <StyledContainer>
        <StyledHeader>
          <StyledHeaderTop>
            <StyledTabContainer>
              <StyledTab
                active={activeTab === 'formula'}
                onClick={() => {
                  // Check for unsaved changes when switching from Joydoc to Formula
                  if (activeTab === 'joydoc' && hasJoydocChanges()) {
                    const confirmed = window.confirm(
                      'Unsaved Changes Will Be Lost. Are you sure you want to switch tabs?'
                    );
                    if (!confirmed) return;
                  }
                  setActiveTab('formula');
                }}
              >
                <StyledStatusBadge>Beta</StyledStatusBadge>
                Formulas
                {activeTab === 'formula' && (
                  <StyledStatusBadge status={currentStatus}>
                    {currentStatus === 'valid' && '✓ Valid'}
                    {currentStatus === 'warning' && '⚠ Warning'}
                    {currentStatus === 'error' && '✗ Error'}
                  </StyledStatusBadge>
                )}
              </StyledTab>
              {/* Temporary until we have a better use case and testing for this feature
              <StyledTab
                active={activeTab === 'joydoc'}
                onClick={() => {
                  // Check for unsaved changes when switching from Formula to Joydoc
                  if (activeTab === 'formula' && (pendingFormulaChanges.size > 0 || fieldsWithUnsavedChanges.size > 0)) {
                    const confirmed = window.confirm(
                      'Unsaved Changes Will Be Lost. Are you sure you want to switch tabs?'
                    );
                    if (!confirmed) return;
                  }
                  setActiveTab('joydoc');
                }}
              >
                Joydoc
              </StyledTab>
              */}
            </StyledTabContainer>

            <StyledCloseButton onClick={handleCloseWithWarning}>
              <span>X</span>
            </StyledCloseButton>
          </StyledHeaderTop>

          {activeTab === 'formula' && (
            <StyledSearchContainer>
              <StyledSearchInput
                type="text"
                placeholder="Search fields by title or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <span style={{ fontSize: '12px', color: '#64748b', whiteSpace: 'nowrap' }}>
                  {totalFields} result{totalFields !== 1 ? 's' : ''}
                </span>
              )}
            </StyledSearchContainer>
          )}
        </StyledHeader>

        {activeTab === 'formula' ? (
          <>
            <StyledScrollableContent>
              {/* Non-readable IDs Warning */}
              {!useReadableIds && (
                <StyledNonReadableIdsWarning>
                  <StyledWarningIcon>⚠️</StyledWarningIcon>
                  <StyledWarningText>
                    <strong>Formulas are not available.</strong> Your document is using ObjectIds instead of readable IDs. 
                    To use formulas, please enable readable IDs by setting the <code>useReadableIds</code> prop to <code>true</code> 
                    when initializing your JoyDoc component.
                  </StyledWarningText>
                </StyledNonReadableIdsWarning>
              )}
              
              {/* General Warnings Accordion */}
              {generalWarnings.length > 0 && (
                <StyledGeneralWarningsAccordion>
                  <StyledAccordionHeader
                    onClick={() => setIsGeneralWarningsExpanded(!isGeneralWarningsExpanded)}
                  >
                    <span>
                      ⚠ {generalWarnings.length} General Warning{generalWarnings.length !== 1 ? 's' : ''}
                    </span>
                    <StyledAccordionIcon expanded={isGeneralWarningsExpanded}>
                      ▼
                    </StyledAccordionIcon>
                  </StyledAccordionHeader>
                  <StyledAccordionContent expanded={isGeneralWarningsExpanded}>
                    {generalWarnings.map((warning, idx) => (
                      <StyledGeneralWarningItem key={idx}>
                        <StyledGeneralWarningIcon>⚠</StyledGeneralWarningIcon>
                        <StyledGeneralWarningContent>
                          <StyledGeneralWarningMessage>
                            {warning.message}
                          </StyledGeneralWarningMessage>
                          {(warning.formulaId || warning.fieldId) && (
                            <StyledGeneralWarningDetails>
                              {warning.formulaId && `Formula: ${warning.formulaId}`}
                              {warning.formulaId && warning.fieldId && ' • '}
                              {warning.fieldId && `Field: ${warning.fieldId}`}
                            </StyledGeneralWarningDetails>
                          )}
                        </StyledGeneralWarningContent>
                        {warning.type && (
                          <StyledGeneralWarningType>
                            {warning.type}
                          </StyledGeneralWarningType>
                        )}
                      </StyledGeneralWarningItem>
                    ))}
                  </StyledAccordionContent>
                </StyledGeneralWarningsAccordion>
              )}

              {filteredPages.length === 0 ? (
                <StyledNoFields>
                  <StyledNoFieldsIcon>🔍</StyledNoFieldsIcon>
                  <StyledNoFieldsText>
                    {searchQuery ? 'No fields match your search' : 'No fields found'}
                  </StyledNoFieldsText>
                  <StyledNoFieldsSubtext>
                    {searchQuery ? 'Try a different search term' : 'Add fields to your document to see them here'}
                  </StyledNoFieldsSubtext>
                </StyledNoFields>
              ) : (
                <>
                  {filteredPages.map((page, pageIndex) => (
                    <StyledPageSection key={page._id || pageIndex}>
                      <StyledPageHeader>
                        <StyledPageTitle>
                          {page.name || `Page ${pageIndex + 1}`}
                        </StyledPageTitle>
                        <StyledFieldCount>
                          {page.fields.length} field{page.fields.length !== 1 ? 's' : ''}
                        </StyledFieldCount>
                      </StyledPageHeader>

                      {page.fields.map((field) => (
                        <FieldFormulaEditor
                          key={`${field._id}-${resetKey}`}
                          field={field}
                          beforeDoc={currentDoc}
                          isResolved={isResolved}
                          resolvedResult={resolvedResult}
                          expandedFields={expandedFields}
                          onToggleExpand={toggleFieldExpansion}
                          onUnsavedChange={handleFieldUnsavedChange}
                          onExpressionChange={handleFormulaExpressionChange}
                          onRemoveFormula={handleRemoveFormula}
                        />
                      ))}
                    </StyledPageSection>
                  ))}
                </>
              )}
            </StyledScrollableContent>

            {totalFields > 0 && (
              <StyledFooter>
                <StyledSummary>
                  {isResolved ? (
                    <>
                      Test Results: {changedFields} of {totalFields} fields changed
                      {currentWarnings.length > 0 && ` • ${currentWarnings.length} warning${currentWarnings.length !== 1 ? 's' : ''}`}
                      {fieldsWithUnsavedChanges.size > 0 && (
                        <>
                          {' • '}
                          <span style={{ 
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '4px 10px',
                            backgroundColor: '#e0e7ff',
                            color: '#6366f1',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            border: '1px solid #c7d2fe'
                          }}>
                            {fieldsWithUnsavedChanges.size} unsaved change{fieldsWithUnsavedChanges.size !== 1 ? 's' : ''}
                          </span>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {totalFields} field{totalFields !== 1 ? 's' : ''}
                      {fieldsWithUnsavedChanges.size > 0 && (
                        <>
                          {' • '}
                          <span style={{ 
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '4px 10px',
                            backgroundColor: '#e0e7ff',
                            color: '#6366f1',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600',
                            border: '1px solid #c7d2fe'
                          }}>
                            {fieldsWithUnsavedChanges.size} unsaved change{fieldsWithUnsavedChanges.size !== 1 ? 's' : ''}
                          </span>
                        </>
                      )}
                    </>
                  )}
                  {isResolved && (
                    <StyledExecutionTime>
                      • Test completed in {resolveExecutionTime}ms
                    </StyledExecutionTime>
                  )}
                </StyledSummary>

                <StyledButtonGroup>
                  <StyledButton
                    onClick={handleTest}
                  >
                    Test
                  </StyledButton>
                  
                  <StyledButton
                    onClick={handleSave}
                    className="save"
                    disabled={!onApplyChanges || pendingFormulaChanges.size === 0}
                  >
                    Save
                  </StyledButton>
                  
                  <StyledButton
                    onClick={handleCloseWithWarning}
                    style={{ backgroundColor: '#64748b' }}
                  >
                    Close
                  </StyledButton>
                </StyledButtonGroup>
              </StyledFooter>
            )}
          </>
        ) : (
          <StyledJoydocContainer>
            <StyledJoydocContent>
              {isEditingJoydoc ? (
                <StyledJsonEditor
                  value={editedJoydoc}
                  onChange={(e) => setEditedJoydoc(e.target.value)}
                  placeholder="Paste your JSON here..."
                />
              ) : (
                <StyledJsonDisplay>
                  {docWithPendingChanges ? JSON.stringify(docWithPendingChanges, null, 2) : 'No document available'}
                </StyledJsonDisplay>
              )}
            </StyledJoydocContent>
            <StyledFooter>
              <StyledSummary>
                <StyledExecutionTime>
                  All changes saved are applied to the document
                </StyledExecutionTime>
              </StyledSummary>
              <StyledButtonGroup>
                {isEditingJoydoc ? (
                  <>
                    <StyledEditButton 
                      onClick={handleSaveJoydocEdit}
                      className="save"
                    >
                      💾 Save
                    </StyledEditButton>
                    <StyledEditButton 
                      onClick={handleCancelJoydocEdit}
                      className="cancel"
                    >
                      ✕ Cancel
                    </StyledEditButton>
                  </>
                ) : (
                  <StyledEditButton onClick={handleEditJoydoc}>
                    Edit
                  </StyledEditButton>
                )}
              </StyledButtonGroup>
            </StyledFooter>
          </StyledJoydocContainer>
        )}
      </StyledContainer>
    </Modal>
  );
} 